import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Review } from "@/types/reviews";

export const useReviewsManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRating, setFilterRating] = useState<number | "">("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortColumn, setSortColumn] = useState<'service_name' | 'customer_name' | 'rating' | 'created_at' | 'comment' | 'highlighted' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const { data: reviews, isLoading } = useQuery({
    queryKey: ["admin-reviews", searchTerm, filterRating],
    queryFn: async () => {
      try {
        const { data: reviewsData, error: reviewsError } = await supabase
          .from("reviews")
          .select(`
            *,
            services(name)
          `)
          .order("created_at", { ascending: false });
        
        if (reviewsError) {
          console.error("Error fetching reviews:", reviewsError);
          throw reviewsError;
        }

        if (!reviewsData || reviewsData.length === 0) {
          console.log("No reviews found in the database");
          return [];
        }

        console.log("Reviews data from API:", reviewsData);

        const processedReviews = await Promise.all(reviewsData.map(async (review) => {
          let customerName = "Anonymous User";
          
          if (review.customer_id) {
            try {
              const { data: profileData, error: profileError } = await supabase
                .from("profiles")
                .select("display_name")
                .eq("id", review.customer_id)
                .maybeSingle();
              
              if (!profileError && profileData && profileData.display_name) {
                customerName = profileData.display_name;
              }
            } catch (err) {
              console.error("Error fetching profile:", err);
            }
          }
          
          // Force highlighted to be a boolean
          const highlighted = review.highlighted === true;
          
          return {
            ...review,
            service_name: review.services?.name || "Unknown Service",
            customer_name: customerName,
            highlighted: highlighted
          };
        }));

        return processedReviews as Review[];
      } catch (error) {
        console.error("Error in reviews query function:", error);
        return [];
      }
    },
  });

  const deleteReviewMutation = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from("reviews")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-reviews"] });
      queryClient.invalidateQueries({ queryKey: ["review-count"] });
      if (selectedReview?.service_id) {
        queryClient.invalidateQueries({ queryKey: ["reviews", selectedReview.service_id] });
      }
      toast({ title: "Success", description: "Review deleted successfully" });
      setIsDetailOpen(false);
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const updateReviewMutation = useMutation({
    mutationFn: async ({ id, highlighted }: { id: number, highlighted: boolean }) => {
      console.log(`Updating review ${id} with highlighted=${highlighted} (${typeof highlighted})`);
      
      // Ensure we're passing a true boolean to the database
      const { error, data } = await supabase
        .from("reviews")
        .update({ highlighted: highlighted })
        .eq("id", id)
        .select();
      
      if (error) {
        console.error("Error updating review:", error);
        throw error;
      }
      
      console.log("Update response:", data);
      return data;
    },
    onSuccess: (data, variables) => {
      console.log("Update success with variables:", variables);
      console.log("Received data from server:", data);
      
      // Update the selected review in state if it's open
      if (selectedReview && selectedReview.id === variables.id) {
        setSelectedReview({
          ...selectedReview,
          highlighted: variables.highlighted
        });
      }
      
      // Invalidate all necessary queries to update data
      queryClient.invalidateQueries({ queryKey: ["admin-reviews"] });
      
      // If we have a service_id, invalidate that specific service's reviews
      if (selectedReview?.service_id) {
        queryClient.invalidateQueries({ queryKey: ["reviews", selectedReview.service_id] });
      }
      
      toast({ 
        title: "Success", 
        description: variables.highlighted 
          ? "Review highlighted successfully" 
          : "Review unhighlighted successfully" 
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const handleRefresh = () => {
    setIsRefreshing(true);
    queryClient.invalidateQueries({ queryKey: ["admin-reviews"] }).then(() => {
      setIsRefreshing(false);
      toast({
        title: "Refreshed",
        description: "Review data has been refreshed"
      });
    });
  };

  const handleViewDetails = (review: Review) => {
    // Make sure highlighted is a boolean
    const reviewWithCorrectHighlight = {
      ...review,
      highlighted: review.highlighted === true
    };
    setSelectedReview(reviewWithCorrectHighlight);
    setIsDetailOpen(true);
  };

  const handleHighlightToggle = (review: Review, checked: boolean) => {
    console.log(`Toggling highlight for review ${review.id} from ${review.highlighted} to ${checked}`);
    
    // Call the mutation with the review ID and new highlighting status
    updateReviewMutation.mutate({ 
      id: review.id, 
      highlighted: checked 
    });
  };

  const handleSort = (column: 'service_name' | 'customer_name' | 'rating' | 'created_at' | 'comment' | 'highlighted' | null) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  const renderSortIndicator = (column: string | null) => {
    if (sortColumn === column) {
      return sortDirection === 'asc' ? 
        { __html: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 ml-1"><path d="m5 15 7-7 7 7"></path></svg>' } : 
        { __html: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 ml-1"><path d="m19 9-7 7-7-7"></path></svg>' };
    }
    return null;
  };

  const filteredReviews = reviews?.filter(review => {
    const matchesSearch = searchTerm === "" || 
      (review.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
       review.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       review.comment?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRating = filterRating === "" || review.rating === filterRating;
    
    return matchesSearch && matchesRating;
  }) || [];

  const sortedReviews = [...filteredReviews].sort((a, b) => {
    if (!sortColumn) return 0;
    
    let aValue: any = a[sortColumn] || '';
    let bValue: any = b[sortColumn] || '';
    
    if (sortColumn === 'comment') {
      aValue = a.comment || '';
      bValue = b.comment || '';
    }
    
    let comparison = 0;
    if (sortColumn === 'rating' || sortColumn === 'created_at') {
      if (sortColumn === 'created_at') {
        comparison = new Date(aValue as string).getTime() - new Date(bValue as string).getTime();
      } else {
        comparison = Number(aValue) - Number(bValue);
      }
    } else if (sortColumn === 'highlighted') {
      // Ensure boolean comparison for highlighted field
      const aHighlighted = aValue === true ? 1 : 0;
      const bHighlighted = bValue === true ? 1 : 0;
      comparison = aHighlighted - bHighlighted;
    } else {
      comparison = String(aValue).localeCompare(String(bValue));
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.ceil(sortedReviews.length / ITEMS_PER_PAGE);
  const paginatedReviews = sortedReviews.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return {
    isLoading,
    reviews: paginatedReviews,
    filteredReviews,
    sortedReviews,
    isDetailOpen,
    setIsDetailOpen,
    selectedReview,
    setSelectedReview,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    filterRating,
    setFilterRating,
    isRefreshing,
    sortColumn,
    sortDirection,
    totalPages,
    handleRefresh,
    handleViewDetails,
    handleHighlightToggle,
    handleSort,
    renderSortIndicator,
    deleteReviewMutation,
  };
};
